import { motion } from "framer-motion";
import { Calendar, Coins, MapPin, Ruler, CheckCircle2 } from "lucide-react";

/**
 * CaseStudies — 2-3 real completed projects with concrete numbers.
 *
 * Emmy's insight: "Namiesto 'Skvelá služba - Ján' použite konkrétny výsledok".
 * Case study format: budget in vs. budget out, timeline promised vs. delivered,
 * client name + role, one honest 1-sentence quote.
 *
 * TODO for client: replace placeholder photos with real /before-after images
 * (bulk upload feature is already in the backlog).
 */
const CASE_STUDIES = [
  {
    id: "senec-rd-2024",
    location: "Senec",
    type: "Rodinný dom na kľúč",
    surface: "165 m²",
    duration: { promised: "13 mesiacov", actual: "12 mesiacov a 3 týždne" },
    budget: { promised: "232 400 €", actual: "232 400 €", note: "0 € preplatku" },
    clientQuote: "Denné fotky som ukazovala v práci — kolegovia neverili, že to ide takto rýchlo a bez stresu.",
    clientName: "Ing. Zuzana K.",
    clientRole: "manažérka projektov · IT",
    image: "https://images.pexels.com/photos/280222/pexels-photo-280222.jpeg?auto=compress&w=800",
    tags: ["Nízkoenergetický A1", "Tepelné čerpadlo", "Fotovoltaika"],
  },
  {
    id: "bratislava-byt-2024",
    location: "Bratislava · Ružinov",
    type: "Rekonštrukcia 3-izbového bytu",
    surface: "72 m²",
    duration: { promised: "9 týždňov", actual: "8 týždňov" },
    budget: { promised: "38 900 €", actual: "39 240 €", note: "+ 340 € (klientom schválené doplnky)" },
    clientQuote: "Bál som sa, že to bude peklo. Realita: každý deň fotky, každý piatok zúčtovanie. Zvládli to lepšie ako sľubovali.",
    clientName: "Michal P.",
    clientRole: "advokát",
    image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&w=800",
    tags: ["Panelák · jadro", "Podlahové kúrenie", "Sadrokartón"],
  },
  {
    id: "dunajska-streda-2023",
    location: "Dunajská Streda",
    type: "Holodom (hrubá stavba)",
    surface: "142 m²",
    duration: { promised: "4 mesiace", actual: "4 mesiace" },
    budget: { promised: "139 200 €", actual: "139 200 €", note: "0 € preplatku" },
    clientQuote: "Cena z ponuky = cena z faktúry. To sa u iných firiem nestáva.",
    clientName: "Rodina Kovácsových",
    clientRole: "investor · rodinný projekt",
    image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&w=800",
    tags: ["Porotherm 44", "Bez poddodávok", "Vlastný tím"],
  },
];

export default function CaseStudies() {
  return (
    <section
      id="pripady"
      className="py-20 bg-[#0a0a0a] relative overflow-hidden"
      data-testid="case-studies-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-12 max-w-3xl">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5E00] font-black mb-3">
            Prípadové štúdie · Rozpočet · Termín · Konkrétni klienti
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Ukazujeme <span className="text-[#00E5FF] neon-cyan">čísla</span>,
            <br />
            <span className="text-zinc-500">nie sľuby</span>
            <span className="text-[#FF5E00]">.</span>
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg mt-4">
            Rozpočet podpísaný v zmluve vs. konečná faktúra. Termín sľúbený vs.
            reálne odovzdanie. Bez skrášľovania.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {CASE_STUDIES.map((cs, i) => (
            <motion.article
              key={cs.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black/50 hover:border-white/20 transition-all"
              data-testid={`case-study-${cs.id}`}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={cs.image}
                  alt={`${cs.type} — ${cs.location}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-white/10 rounded-full text-[10px] mono uppercase tracking-widest text-white">
                  <MapPin size={11} className="text-[#00E5FF]" />
                  {cs.location}
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-xs mono uppercase tracking-widest text-[#00E5FF] mb-1">
                    {cs.type}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-300">
                    <span className="inline-flex items-center gap-1">
                      <Ruler size={11} /> {cs.surface}
                    </span>
                  </div>
                </div>
              </div>

              {/* Numbers */}
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">
                      <Calendar size={10} /> Termín
                    </div>
                    <div className="text-xs text-zinc-400 line-through">{cs.duration.promised}</div>
                    <div className="text-sm font-bold text-emerald-400 mt-0.5 flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      {cs.duration.actual}
                    </div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">
                      <Coins size={10} /> Rozpočet
                    </div>
                    <div className="text-xs text-zinc-400">{cs.budget.promised}</div>
                    <div className="text-sm font-bold text-white mt-0.5">
                      {cs.budget.actual}
                    </div>
                    <div className="text-[10px] text-emerald-400 mt-0.5">{cs.budget.note}</div>
                  </div>
                </div>

                {/* Client quote */}
                <blockquote className="border-l-2 border-[#FF5E00]/60 pl-3">
                  <p className="text-sm text-zinc-200 leading-relaxed italic">
                    &bdquo;{cs.clientQuote}&ldquo;
                  </p>
                  <footer className="mt-2 text-xs text-zinc-500">
                    — <span className="text-white font-medium">{cs.clientName}</span>, {cs.clientRole}
                  </footer>
                </blockquote>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {cs.tags.map((t) => (
                    <span key={t} className="text-[10px] mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-zinc-400 border border-white/5">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <a
            href="/projekty"
            className="inline-flex items-center gap-2 text-sm text-[#00E5FF] hover:text-white transition-colors"
            data-testid="case-studies-more"
          >
            Pozrieť všetkých 172 dokončených projektov na mape →
          </a>
        </motion.div>
      </div>
    </section>
  );
}
